import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ErrorBoundary, Toaster } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import { ProjectProvider } from '@/context/ProjectContext';
import { AuthProvider } from '@/features/auth/AuthContext';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { queryClient } from '@/lib/query-client';

import Layout from './components/Layout';
import StageGuard from './components/StageGuard';

// Eager — used on every authenticated route (avoid flicker)

// Login is eager so cold-start to login is a single chunk (no spinner flash)
import LoginPage from './pages/LoginPage';
import ProjectOverview from './pages/ProjectOverview';

// Lazy routes — code-split by feature
const ActiveDevices = lazy(() => import('./pages/ActiveDevices'));
const ActivityLog = lazy(() => import('./pages/ActivityLog'));
const AIInsights = lazy(() => import('./pages/AIInsights'));
const AssetRegister = lazy(() => import('./pages/AssetRegister'));
const Assignments = lazy(() => import('./pages/Assignments'));
const BuildingDetail = lazy(() => import('./pages/BuildingDetail'));
const BuildingsProgress = lazy(() => import('./pages/BuildingsProgress'));
const ClientReview = lazy(() => import('./pages/ClientReview'));
const Componentization = lazy(() => import('./pages/Componentization'));
const ConflictQueue = lazy(() => import('./pages/ConflictQueue'));
const DeferredQueue = lazy(() => import('./pages/DeferredQueue'));
const ExportPage = lazy(() => import('./pages/ExportPage'));
const FinalDelivery = lazy(() => import('./pages/FinalDelivery'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const LocationEditor = lazy(() => import('./pages/LocationEditor'));
const ProjectHub = lazy(() => import('./pages/ProjectHub'));
const ProjectSetup = lazy(() => import('./pages/ProjectSetup'));
const ProjectWizard = lazy(() => import('./pages/ProjectWizard'));
const QAReview = lazy(() => import('./pages/QAReview'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Reconciliation = lazy(() => import('./pages/Reconciliation'));
const TagsPage = lazy(() => import('./pages/TagsPage'));
const Team = lazy(() => import('./pages/Team'));

/** Shown while a lazy chunk is being fetched. */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path={ROUTES.LOGIN} element={<LoginPage />} />
                <Route
                  path={ROUTES.PROJECTS}
                  element={
                    <ProtectedRoute>
                      <ProjectHub />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.PROJECTS_NEW}
                  element={
                    <ProtectedRoute>
                      <ProjectWizard />
                    </ProtectedRoute>
                  }
                />
                {/* Client review — see DOSSIE SEC-03: token gating scaffolded in ClientReview itself */}
                <Route path="/client-review/:projectId" element={<ClientReview />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <ProjectProvider>
                        <Routes>
                          <Route element={<Layout />}>
                            <Route path="setup" element={<ProjectSetup />} />
                            <Route path="import" element={<ImportPage />} />
                            <Route
                              path="classifications"
                              element={<Navigate to="/import?tab=classifications" replace />}
                            />
                            <Route path="locations" element={<LocationEditor />} />
                            <Route index element={<ProjectOverview />} />
                            <Route path="register" element={<AssetRegister />} />
                            <Route path="tags" element={<TagsPage />} />
                            <Route path="buildings" element={<BuildingsProgress />} />
                            <Route path="buildings/:id" element={<BuildingDetail />} />
                            <Route path="team" element={<Team />} />
                            <Route path="devices" element={<ActiveDevices />} />
                            <Route path="assignments" element={<Assignments />} />
                            <Route
                              path="users"
                              element={<Navigate to="/team?tab=members" replace />}
                            />
                            <Route
                              path="qa"
                              element={
                                <StageGuard minStage="qa_review" label="QA Review">
                                  <QAReview />
                                </StageGuard>
                              }
                            />
                            <Route
                              path="inventory"
                              element={
                                <StageGuard minStage="qa_review" label="Inventory">
                                  <Inventory />
                                </StageGuard>
                              }
                            />
                            <Route
                              path="conflicts"
                              element={
                                <StageGuard minStage="qa_review" label="Conflicts">
                                  <ConflictQueue />
                                </StageGuard>
                              }
                            />
                            <Route
                              path="deferred"
                              element={
                                <StageGuard minStage="qa_review" label="Deferred">
                                  <DeferredQueue />
                                </StageGuard>
                              }
                            />
                            <Route
                              path="reconciliation"
                              element={
                                <StageGuard minStage="qa_review" label="Reconciliation">
                                  <Reconciliation />
                                </StageGuard>
                              }
                            />
                            <Route path="final-delivery" element={<FinalDelivery />} />
                            <Route path="exports" element={<ExportPage />} />
                            <Route path="ai-insights" element={<AIInsights />} />
                            <Route path="componentization" element={<Componentization />} />
                            <Route path="activity-log" element={<ActivityLog />} />
                            <Route
                              path="export"
                              element={<Navigate to="/reconciliation" replace />}
                            />
                          </Route>
                        </Routes>
                      </ProjectProvider>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
